import React, { useEffect, Suspense } from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import { BrowserRouter as Router } from "react-router-dom";
import * as ReactDOM from "react-dom";
import AppRoutes from "./routes";
import { theme } from "./styles/theme";
import { GlobalStyles } from "./styles/GlobalStyles";
import i18n from "./i18n/i18n";
import ThemeProvider from "./context/ThemeContext";
import SkipLink from "./components/accessibility/SkipLink";
import A11yChecker from "./components/accessibility/A11yChecker";
import ErrorBoundary from "./components/common/ErrorBoundary";

const setupAccessibilityTools = async () => {
  if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
    try {
      const axeCoreReact = await import("@axe-core/react").catch(() => null);
      if (axeCoreReact && axeCoreReact.default) {
        setTimeout(() => {
          try {
            axeCoreReact.default(React, ReactDOM, 2000, { rules: [] });
            // eslint-disable-next-line no-console
            console.log("[a11y] @axe-core/react ready");
          } catch (initError) {
            // eslint-disable-next-line no-console
            console.warn("[a11y] init failed", initError);
          }
        }, 1000);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[a11y] @axe-core/react not loaded", e);
    }
  }
};

const RouteFallback: React.FC = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh",
    }}
    aria-busy="true"
    aria-live="polite"
  >
    Carregando…
  </div>
);

const App: React.FC = () => {
  useEffect(() => {
    setupAccessibilityTools();
    const savedLanguage = localStorage.getItem("i18nextLng");
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <StyledThemeProvider theme={theme}>
          <GlobalStyles />
          <Router>
            <SkipLink />
            <div id="main-content">
              <Suspense fallback={<RouteFallback />}>
                <AppRoutes />
              </Suspense>
            </div>
            {process.env.NODE_ENV !== "production" && <A11yChecker />}
          </Router>
        </StyledThemeProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
